import { transition } from '@helpers/animate';
import { imageCoverContainer, imageWrapperPreserveRatio } from '@helpers/aspectRatio';
import brand, { BrandColours } from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { Device, from, until } from '@helpers/media';
import { contentStyles, reverseLinkUnderline } from '@helpers/typography';
import ButtonStyles from '@stories/Components/Buttons/Button/Button.styles';
import styled from 'styled-components';
const Container = styled.div `
  --textLinkColour: ${brand.global.white};

  min-height: 100px;
  height: 100%;
  border-radius: 10px;
  color: var(--textLinkColour);
  overflow: hidden;
  padding: ${fluid(24, 32)} ${fluid(16, 26)};
  background-color: var(--contentColour);

  &[data-variant=${BrandColours.BlueDark}] {
    --contentColour: ${brand.primary.blue.dark};
    --imageColour: ${brand.primary.blue.light};
  }

  &[data-variant=${BrandColours.PurpleLight}] {
    --contentColour: ${brand.primary.purple.light};
    --imageColour: ${brand.primary.blue.light};
  }
`;
const Grid = styled.div `
  display: grid;
  grid-template-columns: ${fluid(82, 122)} auto;
  grid-template-rows: min-content auto;
  gap: 0 ${fluid(12, 32)};
  height: 100%;

  @media ${until(Device.TabletLarge)} {
    align-items: center;
  }

  &[data-has-company='true'] {
    @media ${until(Device.TabletLarge)} {
      align-items: flex-start;
    }
  }
`;
const ImageContainer = styled.div `
  ${imageWrapperPreserveRatio(1, 1)};

  display: grid;
  place-content: center;
  background-color: var(--imageColour);
  width: ${fluid(82, 122)};
  height: ${fluid(82, 122)};
  border-radius: 50%;
  grid-column: 1;
  grid-row: 1;

  @media ${from(Device.TabletLarge)} {
    grid-row: span 2;
  }
`;
const Image = styled.img `
  ${imageCoverContainer()};
`;
const ProfileArea = styled.div `
  grid-column: 2;
  grid-row: 1;
  margin: 0 0 24px;

  @media ${until(Device.TabletLarge)} {
    margin-bottom: 0;
  }
`;
const Heading = styled.h3 `
  ${fonts.intro};

  font-size: ${fluid(20, 24)};
  line-height: ${fluid(26, 28)};
  margin: 0;
  text-transform: uppercase;
`;
const Subheading = styled.p `
  ${fonts.lato.semiBold};

  font-size: ${fluid(14, 18)};
  line-height: ${fluid(22, 28)};

  @media ${until(Device.TabletLarge)} {
    ${fonts.lato.regular};
  }
`;
const Company = styled.p `
  ${fonts.lato.regular};

  font-size: ${fluid(14, 16)};
  line-height: ${fluid(22, 24)};
  margin-top: ${fluid(12, 20)};
`;
const MainContentHolder = styled.div `
  display: flex;
  grid-column: 2;
  flex-direction: column;

  @media ${until(Device.TabletLarge)} {
    grid-row: 2;
    grid-column: 1 / span 2;
  }
`;
const Content = styled.div `
  ${contentStyles};

  @media ${until(Device.TabletLarge)} {
    margin-top: 16px;
  }

  a {
    ${reverseLinkUnderline};
  }

  p {
    font-size: ${fluid(14, 16)};
    line-height: ${fluid(24, 24)};
  }
`;
const ContactArea = styled.div `
  display: flex;
  flex-direction: column;
  padding-top: 20px;
  gap: 10px;
  margin-top: auto;
`;
const LinkedIn = styled.a `
  ${fonts.lato.regular};
  font-size: ${fluid(14, 16)};
  line-height: ${fluid(24, 24)};
  color: ${brand.global.white};
  display: inline-flex;
  align-items: center;
  text-decoration: none;
  gap: ${fluid(6, 12)};
  position: relative;
  width: fit-content;

  &:after {
    ${transition('transform')}
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    height: 1px;
    width: 100%;
    background-color: ${brand.global.white};
    transform: scaleX(0);
    transform-origin: left;
  }

  &:hover,
  &:focus {
    &:after {
      transform: scaleX(1);
    }
  }

  svg {
    height: ${fluid(24, 32)};
    width: ${fluid(24, 32)};
  }
`;
const TelNumber = styled.a `
  ${fonts.lato.regular};
  font-size: ${fluid(14, 16)};
  line-height: ${fluid(24, 24)};
  color: ${brand.global.white};
  display: inline-flex;
  align-items: center;
  text-decoration: none;
  gap: ${fluid(6, 12)};
  position: relative;
  width: fit-content;

  &:after {
    ${transition('transform')}
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    height: 1px;
    width: 100%;
    background-color: ${brand.global.white};
    transform: scaleX(0);
    transform-origin: left;
  }

  &:hover,
  &:focus {
    &:after {
      transform: scaleX(1);
    }
  }

  svg {
    height: ${fluid(24, 32)};
    width: ${fluid(24, 32)};
  }
`;
const CtaArea = styled.div `
  padding-top: 20px;
  display: flex;
  flex-wrap: wrap;
  gap: ${fluid(16, 20)};

  ${Container}:not(:has(${ContactArea})) & {
    margin-top: auto;
  }

  @media ${until(Device.Tablet)} {
    padding-top: 24px;
    flex-direction: column;
  }

  ${ButtonStyles.Container} {
    --buttonHeight: ${fluid(44, 48)};
    position: relative;
    align-self: flex-start;
  }
`;
const PersonCardStyles = {
    Company,
    ContactArea,
    Container,
    Content,
    CtaArea,
    ImageContainer,
    Image,
    Grid,
    Heading,
    LinkedIn,
    MainContentHolder,
    ProfileArea,
    Subheading,
    TelNumber,
};
export default PersonCardStyles;
